import ExcelJS from "exceljs";

const thinBorder = {
  top: { style: "thin", color: { argb: "FFD1D5DB" } },
  left: { style: "thin", color: { argb: "FFD1D5DB" } },
  bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
  right: { style: "thin", color: { argb: "FFD1D5DB" } },
};

const COLORS = {
  titleBg: "FFB91C1C",
  titleFg: "FFFFFFFF",
  metaBg: "FFF9FAFB",
  metaFg: "FF374151",
  summaryBg: "FFFEF2F2",
  summaryFg: "FF7F1D1D",
  headerBg: "FF991B1B",
  headerFg: "FFFFFFFF",
  zebraBg: "FFF9FAFB",
  presentFg: "FF047857",
  lateFg: "FFB45309",
  absentFg: "FFDC2626",
  rateGoodBg: "FFD1FAE5",
  rateGoodFg: "FF065F46",
  rateMidBg: "FFFEF3C7",
  rateMidFg: "FF92400E",
  rateLowBg: "FFFEE2E2",
  rateLowFg: "FF991B1B",
};

function rateFill(rate) {
  const n = Number(rate) || 0;
  if (n >= 90) return { bg: COLORS.rateGoodBg, fg: COLORS.rateGoodFg };
  if (n >= 70) return { bg: COLORS.rateMidBg, fg: COLORS.rateMidFg };
  return { bg: COLORS.rateLowBg, fg: COLORS.rateLowFg };
}

function formatYearMonthVi(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return null;
  const [y, m] = ym.split("-");
  return `Tháng ${Number(m)}/${y}`;
}

function periodLabel(meta = {}) {
  const ym = formatYearMonthVi(meta.year_month);
  if (ym) return ym;
  if (meta.date_from) {
    return `Từ ${meta.date_from}${meta.date_to ? ` đến ${meta.date_to}` : ""}`;
  }
  return "Toàn bộ thời gian";
}

function applyTitleCell(cell, text) {
  cell.value = text;
  cell.font = { bold: true, size: 14, color: { argb: COLORS.titleFg }, name: "Calibri" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: COLORS.titleBg },
  };
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  cell.border = thinBorder;
}

function applyMetaCell(cell, text) {
  cell.value = text;
  cell.font = { size: 11, color: { argb: COLORS.metaFg }, name: "Calibri" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: COLORS.metaBg },
  };
  cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
  cell.border = thinBorder;
}

function applySummaryCell(cell, text) {
  cell.value = text;
  cell.font = { bold: true, size: 11, color: { argb: COLORS.summaryFg }, name: "Calibri" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: COLORS.summaryBg },
  };
  cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
  cell.border = thinBorder;
}

function applyHeaderCell(cell, text) {
  cell.value = text;
  cell.font = { bold: true, size: 11, color: { argb: COLORS.headerFg }, name: "Calibri" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: COLORS.headerBg },
  };
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  cell.border = thinBorder;
}

function styleDataCell(cell, { align = "left", bold = false, color, fill, numFmt } = {}) {
  cell.font = {
    size: 11,
    bold,
    name: "Calibri",
    ...(color ? { color: { argb: color } } : {}),
  };
  cell.alignment = { horizontal: align, vertical: "middle", wrapText: true };
  cell.border = thinBorder;
  if (fill) {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
  }
  if (numFmt) cell.numFmt = numFmt;
}

async function downloadWorkbook(wb, filename) {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Xuất báo cáo điểm danh ra Excel (có định dạng).
 * view=classes: tổng hợp theo lớp
 * view=students: chi tiết học viên trong lớp
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export async function exportAttendanceReportToExcel({
  view = "classes",
  records = [],
  absenceRecords = [],
  summary = {},
  meta = {},
  filterLabel = "",
  classInfo = null,
} = {}) {
  if (!records.length) {
    return { ok: false, message: "Không có dữ liệu để xuất Excel." };
  }

  const isStudents = view === "students";
  const stamp = new Date().toISOString().slice(0, 10);
  const ym = meta.year_month ? `_${meta.year_month}` : "";
  const exportedAt = new Date().toLocaleString("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
  });

  const wb = new ExcelJS.Workbook();
  wb.creator = "IMA ERP";
  wb.created = new Date();

  if (isStudents) {
    const headers = [
      "STT",
      "Mã HV",
      "Họ tên",
      "Có mặt",
      "Muộn",
      "Vắng",
      "Vắng CP",
      "Vắng KP",
      "Tổng buổi",
      "Tỷ lệ chuyên cần (%)",
    ];
    const numCols = headers.length;
    const ws = wb.addWorksheet("Chi tiết HV", {
      views: [{ state: "frozen", ySplit: 5 }],
    });

    const title = "BÁO CÁO ĐIỂM DANH — CHI TIẾT HỌC VIÊN";
    const classLine = classInfo?.class_name
      ? `Lớp: ${classInfo.class_name}${
          classInfo.class_code ? ` (${classInfo.class_code})` : ""
        }${classInfo.course_name ? ` · Khóa: ${classInfo.course_name}` : ""}`
      : "Chi tiết học viên";
    const metaLine = [
      `Kỳ báo cáo: ${periodLabel(meta)}`,
      filterLabel || null,
      `Xuất lúc: ${exportedAt}`,
    ]
      .filter(Boolean)
      .join("  |  ");
    const summaryLine = [
      `Học viên: ${summary.students ?? records.length}`,
      `Buổi điểm danh: ${summary.sessions ?? 0}`,
      `Có mặt: ${summary.present ?? 0}`,
      `Muộn: ${summary.late ?? 0}`,
      `Vắng: ${summary.absent ?? 0}`,
    ].join("   ·   ");

    ws.mergeCells(1, 1, 1, numCols);
    applyTitleCell(ws.getCell(1, 1), title);
    ws.getRow(1).height = 28;

    ws.mergeCells(2, 1, 2, numCols);
    applyMetaCell(ws.getCell(2, 1), classLine);
    ws.getRow(2).height = 20;

    ws.mergeCells(3, 1, 3, numCols);
    applyMetaCell(ws.getCell(3, 1), metaLine);
    ws.getRow(3).height = 20;

    ws.mergeCells(4, 1, 4, numCols);
    applySummaryCell(ws.getCell(4, 1), summaryLine);
    ws.getRow(4).height = 22;

    const headerRow = ws.getRow(5);
    headers.forEach((h, i) => applyHeaderCell(headerRow.getCell(i + 1), h));
    headerRow.height = 24;

    records.forEach((r, idx) => {
      const row = ws.getRow(6 + idx);
      row.height = 20;
      const zebra = idx % 2 === 1 ? COLORS.zebraBg : null;
      const rate = Number(r.attendance_rate) || 0;
      const rateStyle = rateFill(rate);

      const values = [
        idx + 1,
        r.student_code || "",
        r.student_name || "",
        r.present_count ?? 0,
        r.late_count ?? 0,
        r.absent_count ?? 0,
        r.absent_excused_count ?? 0,
        r.absent_unexcused_count ?? 0,
        r.total_sessions ?? 0,
        rate,
      ];

      values.forEach((val, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        cell.value = val;
        if (colIndex === 0) {
          styleDataCell(cell, { align: "center", fill: zebra });
        } else if (colIndex === 1) {
          styleDataCell(cell, { align: "center", fill: zebra });
        } else if (colIndex === 2) {
          styleDataCell(cell, { align: "left", bold: true, fill: zebra });
        } else if (colIndex === 3) {
          styleDataCell(cell, {
            align: "center",
            bold: true,
            color: COLORS.presentFg,
            fill: zebra,
          });
        } else if (colIndex === 4) {
          styleDataCell(cell, {
            align: "center",
            bold: true,
            color: COLORS.lateFg,
            fill: zebra,
          });
        } else if (colIndex === 5) {
          styleDataCell(cell, {
            align: "center",
            bold: true,
            color: COLORS.absentFg,
            fill: zebra,
          });
        } else if (colIndex === 6) {
          styleDataCell(cell, {
            align: "center",
            bold: true,
            color: "FF0284C7",
            fill: zebra,
          });
        } else if (colIndex === 7) {
          styleDataCell(cell, {
            align: "center",
            bold: true,
            color: COLORS.absentFg,
            fill: zebra,
          });
        } else if (colIndex === 8) {
          styleDataCell(cell, { align: "center", bold: true, fill: zebra });
        } else {
          styleDataCell(cell, {
            align: "center",
            bold: true,
            color: rateStyle.fg,
            fill: rateStyle.bg,
            numFmt: "0.0",
          });
        }
      });
    });

    const widths = [6, 14, 28, 10, 10, 10, 10, 10, 12, 18];
    widths.forEach((w, i) => {
      ws.getColumn(i + 1).width = w;
    });

    ws.autoFilter = {
      from: { row: 5, column: 1 },
      to: { row: 5 + records.length, column: numCols },
    };

    const absenceRows =
      absenceRecords.length > 0
        ? absenceRecords
        : records.filter((r) => Number(r.absent_count) > 0);

    if (absenceRows.length > 0) {
      const absenceHeaders = [
        "STT",
        "Mã HV",
        "Họ tên",
        "Tổng vắng",
        "Vắng CP",
        "Vắng KP",
        "Chi tiết ngày vắng",
        "Ghi chú GV",
      ];
      const absenceCols = absenceHeaders.length;
      const wsAbsence = wb.addWorksheet("Chi tiết vắng mặt", {
        views: [{ state: "frozen", ySplit: 4 }],
      });

      wsAbsence.mergeCells(1, 1, 1, absenceCols);
      applyTitleCell(wsAbsence.getCell(1, 1), "CHI TIẾT VẮNG MẶT THEO HỌC VIÊN");
      wsAbsence.getRow(1).height = 28;

      wsAbsence.mergeCells(2, 1, 2, absenceCols);
      applyMetaCell(
        wsAbsence.getCell(2, 1),
        [
          classLine,
          `Kỳ báo cáo: ${periodLabel(meta)}`,
          filterLabel || null,
        ]
          .filter(Boolean)
          .join("  |  ")
      );
      wsAbsence.getRow(2).height = 20;

      wsAbsence.mergeCells(3, 1, 3, absenceCols);
      applySummaryCell(
        wsAbsence.getCell(3, 1),
        `Học viên có vắng: ${absenceRows.length}  |  Xuất lúc: ${exportedAt}`
      );
      wsAbsence.getRow(3).height = 22;

      const absenceHeaderRow = wsAbsence.getRow(4);
      absenceHeaders.forEach((h, i) =>
        applyHeaderCell(absenceHeaderRow.getCell(i + 1), h)
      );
      absenceHeaderRow.height = 24;

      absenceRows.forEach((r, idx) => {
        const row = wsAbsence.getRow(5 + idx);
        row.height = 22;
        const zebra = idx % 2 === 1 ? COLORS.zebraBg : null;
        const values = [
          idx + 1,
          r.student_code || "",
          r.student_name || "",
          r.absent_count ?? 0,
          r.absent_excused_count ?? 0,
          r.absent_unexcused_count ?? 0,
          r.absence_summary_text || "",
          r.absence_teacher_notes || "",
        ];
        values.forEach((val, colIndex) => {
          const cell = row.getCell(colIndex + 1);
          cell.value = val;
          if (colIndex === 0 || colIndex === 1) {
            styleDataCell(cell, { align: "center", fill: zebra });
          } else if (colIndex === 2) {
            styleDataCell(cell, { align: "left", bold: true, fill: zebra });
          } else if ([3, 4, 5].includes(colIndex)) {
            styleDataCell(cell, { align: "center", bold: true, fill: zebra });
          } else {
            styleDataCell(cell, { align: "left", fill: zebra });
          }
        });
      });

      const absenceWidths = [6, 14, 28, 10, 10, 10, 42, 36];
      absenceWidths.forEach((w, i) => {
        wsAbsence.getColumn(i + 1).width = w;
      });
    }

    await downloadWorkbook(
      wb,
      `bao_cao_dd_${classInfo?.class_code || "lop"}${ym}_${stamp}.xlsx`
    );
    return { ok: true };
  }

  // ===== Theo lớp =====
  const headers = [
    "STT",
    "Lớp",
    "Mã lớp",
    "Khóa học",
    "Chi nhánh",
    "Số HV",
    "Số buổi",
    "Có mặt",
    "Muộn",
    "Vắng",
    "Tỷ lệ chuyên cần (%)",
  ];
  const numCols = headers.length;
  const ws = wb.addWorksheet("Theo lớp", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  const title = "BÁO CÁO ĐIỂM DANH — TỔNG HỢP THEO LỚP";
  const metaLine = [
    `Kỳ báo cáo: ${periodLabel(meta)}`,
    filterLabel || null,
    `Xuất lúc: ${exportedAt}`,
  ]
    .filter(Boolean)
    .join("  |  ");
  const summaryLine = [
    `Số lớp: ${summary.classes ?? records.length}`,
    `Học viên: ${summary.students ?? 0}`,
    `Buổi điểm danh: ${summary.sessions ?? 0}`,
    `Có mặt: ${summary.present ?? 0}`,
    `Muộn: ${summary.late ?? 0}`,
    `Vắng: ${summary.absent ?? 0}`,
  ].join("   ·   ");

  ws.mergeCells(1, 1, 1, numCols);
  applyTitleCell(ws.getCell(1, 1), title);
  ws.getRow(1).height = 28;

  ws.mergeCells(2, 1, 2, numCols);
  applyMetaCell(ws.getCell(2, 1), metaLine);
  ws.getRow(2).height = 20;

  ws.mergeCells(3, 1, 3, numCols);
  applySummaryCell(ws.getCell(3, 1), summaryLine);
  ws.getRow(3).height = 22;

  const headerRow = ws.getRow(4);
  headers.forEach((h, i) => applyHeaderCell(headerRow.getCell(i + 1), h));
  headerRow.height = 24;

  records.forEach((r, idx) => {
    const row = ws.getRow(5 + idx);
    row.height = 20;
    const zebra = idx % 2 === 1 ? COLORS.zebraBg : null;
    const rate = Number(r.attendance_rate) || 0;
    const rateStyle = rateFill(rate);

    const values = [
      idx + 1,
      r.class_name || "",
      r.class_code || "",
      r.course_name || "",
      r.branch_name || "",
      r.student_count ?? 0,
      r.session_count ?? 0,
      r.present_count ?? 0,
      r.late_count ?? 0,
      r.absent_count ?? 0,
      rate,
    ];

    values.forEach((val, colIndex) => {
      const cell = row.getCell(colIndex + 1);
      cell.value = val;
      if (colIndex === 0) {
        styleDataCell(cell, { align: "center", fill: zebra });
      } else if (colIndex === 1) {
        styleDataCell(cell, { align: "left", bold: true, fill: zebra });
      } else if ([2, 3, 4].includes(colIndex)) {
        styleDataCell(cell, { align: "left", fill: zebra });
      } else if (colIndex === 7) {
        styleDataCell(cell, {
          align: "center",
          bold: true,
          color: COLORS.presentFg,
          fill: zebra,
        });
      } else if (colIndex === 8) {
        styleDataCell(cell, {
          align: "center",
          bold: true,
          color: COLORS.lateFg,
          fill: zebra,
        });
      } else if (colIndex === 9) {
        styleDataCell(cell, {
          align: "center",
          bold: true,
          color: COLORS.absentFg,
          fill: zebra,
        });
      } else if (colIndex === 10) {
        styleDataCell(cell, {
          align: "center",
          bold: true,
          color: rateStyle.fg,
          fill: rateStyle.bg,
          numFmt: "0.0",
        });
      } else {
        styleDataCell(cell, { align: "center", bold: true, fill: zebra });
      }
    });
  });

  const widths = [6, 28, 18, 24, 16, 10, 10, 10, 10, 10, 18];
  widths.forEach((w, i) => {
    ws.getColumn(i + 1).width = w;
  });

  ws.autoFilter = {
    from: { row: 4, column: 1 },
    to: { row: 4 + records.length, column: numCols },
  };

  await downloadWorkbook(wb, `bao_cao_diem_danh_lop${ym}_${stamp}.xlsx`);
  return { ok: true };
}
